import type { ReactNode } from "react";
import { PixelField } from "@/components/PixelField";

/**
 * Общий каркас всех экранов входа: фон-тоннель, плитка логотипа,
 * заголовок, подзаголовок и место под карточку.
 *
 * Каркас владеет всем, что вокруг карточки, а каждый экран приносит
 * только своё содержимое. Поэтому переход между /login,
 * /forgot-password, /reset-password и /change-password не двигает
 * рамку — меняется ровно то, ради чего человек пришёл.
 *
 * До августа 2026 здесь была двухколоночная вёрстка с тёмным
 * промо-блоком слева; заменена по дизайну `logindesign.zip`.
 */

const STAR = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 3.1l2.53 5.72 6.17.55-4.66 4.09 1.39 6.08L12 16.4l-5.43 3.14 1.39-6.08-4.66-4.09 6.17-.55z" />
  </svg>
);

export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  /** Заголовок экрана. Строка или разметка — на /login часть слова
   *  выделена курсивом через `<span className="au-em">`. */
  title: ReactNode;
  subtitle: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="au">
      <div className="px-wrap" aria-hidden="true">
        <PixelField cx={0.5} cy={0.46} />
      </div>
      <div className="au-in">
        <div className="au-head">
          <span className="au-mark">{STAR}</span>
          <h1>{title}</h1>
          <p className="au-sub">{subtitle}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Стрелка в кнопке отправки. */
export const ARROW = (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h13M12.5 6l6 6-6 6" />
  </svg>
);

/** Значок в полосе ошибки. */
export const WARN = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16.5v.01" />
  </svg>
);

/** Галочка на панели успеха. */
export const CHECK = (
  <svg
    width="30"
    height="30"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m5 12 5 5L20 7" />
  </svg>
);

/** Полоса ошибки над карточкой — то, что вернул сервер. */
export function AuthAlert({ children }: { children: ReactNode }) {
  return (
    <div className="au-alert" role="alert">
      {WARN}
      <span>{children}</span>
    </div>
  );
}

/**
 * Панель успеха вместо карточки.
 *
 * Заменяет форму целиком, а не показывается над ней: после успеха
 * поля уже не нужны, а оставленная форма провоцирует отправить второй
 * раз.
 */
export function AuthSuccess({
  title,
  children,
  cta,
}: {
  title: string;
  children: ReactNode;
  cta?: ReactNode;
}) {
  return (
    <div className="au-card au-ok">
      <div className="tick">{CHECK}</div>
      <h2>{title}</h2>
      {children}
      {cta}
    </div>
  );
}

/**
 * Одно поле карточки.
 *
 * Сообщение об ошибке связано с полем через `aria-describedby`, само
 * поле помечается `aria-invalid` — иначе экранный диктор объявит
 * ошибку как отдельный текст неизвестно о чём.
 */
export function AuthField({
  id,
  label,
  type = "text",
  value,
  onChange,
  autoComplete,
  placeholder,
  disabled,
  error,
  children,
}: {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string | null;
  /** Дополнение под полем — например полоса надёжности пароля. */
  children?: ReactNode;
}) {
  return (
    <div className={`field${error ? " err" : ""}`}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-m` : undefined}
      />
      {children}
      <div className="msg" id={`${id}-m`}>
        {error}
      </div>
    </div>
  );
}

/** Кнопка отправки: стрелка в покое, вертушка при отправке. */
export function AuthSubmit({
  busy,
  busyLabel,
  children,
}: {
  busy: boolean;
  busyLabel: string;
  children: ReactNode;
}) {
  return (
    <button
      className="btn primary au-go"
      type="submit"
      disabled={busy}
      aria-busy={busy || undefined}
    >
      {busy ? (
        <>
          <span className="spin" />
          {busyLabel}
        </>
      ) : (
        <>
          {children}
          {ARROW}
        </>
      )}
    </button>
  );
}

/**
 * Полоса надёжности пароля — четыре деления.
 *
 * Оценка нарочно грубая и локальная: длина плюс разнообразие
 * символов. Это подсказка, а не проверка — настоящий отказ по
 * скомпрометированному паролю выносит сервер (список из 38 451
 * пароля), и делать вид, что мы знаем это на клиенте, было бы
 * враньём в интерфейсе.
 */
function passwordStrength(pw: string): 0 | 1 | 2 | 3 | 4 {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  const classes =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/\d/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);
  if (classes >= 2) score++;
  if (classes >= 3) score++;
  return Math.min(4, score) as 0 | 1 | 2 | 3 | 4;
}

const STRENGTH_LABEL = ["", "Weak", "Fair", "Good", "Strong"];

export function PasswordStrength({ value }: { value: string }) {
  const lv = passwordStrength(value);
  return (
    <div className="au-str" data-lv={lv} aria-hidden="true">
      <div className="bar">
        <i />
        <i />
        <i />
        <i />
      </div>
      <span className="lb">{STRENGTH_LABEL[lv] || " "}</span>
    </div>
  );
}
